import { FormEvent } from "react"
import { useModalWindow } from "react-modal-global"
import ZodForm, { useZodFormIssues } from "react-zod-form"
import { coerce } from "zod"

import queryClient from "@/api/client"
import MenuAppDAO from "@/entities/app/menu/menu.dao"
import PopupLayout from "@/services/Modal/layouts/PopupLayout/PopupLayout"
import Button from "@/shared/components/intrinsic/Button/Button"
import Field from "@/shared/components/intrinsic/Field/Field"
import Fields from "@/shared/layouts/Fields/Fields" 
import useFormStatus from "@/utils/hooks/useFormStatus"




interface PopupCreateMenuAppProps {
  companyId: string
  location: {
    id: string
  }

  onCreate?(menuAppId: string): void
}

const form = new ZodForm({
  name: coerce.string().trim().min(1, "AppId cannot be empty. Please enter a valid AppId to proceed."),
  label: coerce.string().min(1, "Name cannot be empty. Please enter a valid name to proceed.")
}, { noTransform: true })
const menuAppDao = new MenuAppDAO

function PopupCreateMenuApp(props: PopupCreateMenuAppProps) {
  const modal = useModalWindow()
  const formStatus = useFormStatus(form, onSubmit)

  async function createMenuApp(name: string, label: string) {
    const menuApp = await menuAppDao.create({
      name,
      label,
      companyId: props.companyId,
      locationId: props.location.id
    })

    const queryKey = [menuAppDao.name, "location", props.location.id]
    queryClient.invalidateQueries({ queryKey })

    return menuApp
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const name = form.parseField(event, "name")
    const label = form.parseField(event, "label")
    if (name == null || label == null) return

    const menuApp = await createMenuApp(name.value, label.value)
    props.onCreate?.(menuApp.id)

    modal.close()
  }

  const { fieldIssues } = useZodFormIssues(form)

  return (
    <PopupLayout title="Create Menu APP Instance">
      <form onChange={form.parseCurrentField.bind(form)} onSubmit={formStatus.submit}>
        <Fields>
          <Field name={form.fields.name} placeholder="App ID" customValidity={fieldIssues.name} />
          <Field name={form.fields.label} placeholder="Name" customValidity={fieldIssues.label} />
          <Button color="white" type="submit" {...formStatus}>Create</Button>
        </Fields>
      </form>
    </PopupLayout>
  )
}

export default PopupCreateMenuApp
